/** P32 (Feb 27, 2026) — date/time math helpers for the timesheet
 *  review screen. Extracted so the parsing logic can be unit-tested
 *  in isolation from the React component tree. */

export const DAY_ORDER = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

export const timeToMinutes = (t) => {
  if (!t) return null;
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?$/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const mi = parseInt(m[2], 10);
  const p = (m[3] || '').toUpperCase();
  if (p === 'AM' && h === 12) h = 0;
  else if (p === 'PM' && h !== 12) h += 12;
  return h * 60 + mi;
};

export const minutesBetween = (a, b) => {
  const x = timeToMinutes(a), y = timeToMinutes(b);
  if (x === null || y === null) return null;
  return y >= x ? y - x : y + 1440 - x;
};

export const minutesToUnits = (m) => {
  if (!m || m <= 0) return 0;
  // 35-59 minutes round to 3 units (45 min); everything else snaps to
  // the nearest 15-minute boundary per ODM Aide-Visit billing conventions.
  if (m >= 35 && m <= 59) return 3;
  return Math.round(m / 15);
};

export const entryUnits = (e) => {
  const m = minutesBetween(e.time_in, e.time_out);
  if (m !== null) return minutesToUnits(m);
  return minutesToUnits((parseFloat(e.total_hours) || 0) * 60);
};

export const parseDateValue = (s) => {
  if (!s) return null;
  let m = String(s).trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) {
    const y = m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10);
    return new Date(y, parseInt(m[1], 10) - 1, parseInt(m[2], 10));
  }
  m = String(s).match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  return null;
};

export const sortChronologically = (entries) => {
  const copy = [...(entries || [])];
  copy.sort((a, b) => {
    const da = parseDateValue(a.date), db = parseDateValue(b.date);
    if (da && db) return da - db;
    if (da) return -1;
    if (db) return 1;
    return (DAY_ORDER[a.day_of_week] ?? 99) - (DAY_ORDER[b.day_of_week] ?? 99);
  });
  return copy;
};

const READINESS_BADGE_TONE = {
  ready: 'bg-emerald-100 text-emerald-700',
  review: 'bg-amber-100 text-amber-700',
};

export const readinessBadgeTone = (level) =>
  READINESS_BADGE_TONE[level] || 'bg-red-100 text-red-700';

export const BACKEND_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;
